import { Component, ElementRef, inject, Input, OnChanges, SimpleChange, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EditorInitEvent, EditorModule } from 'primeng/editor';
import { TreatmentReviewerComponent } from '../treatment-reviewer';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RxHelper } from '../../core/utils/rxjxHelper';
import { ImageClient } from '../../core/client/image-client';
import { firstValueFrom } from 'rxjs';

export const createTreatmentReturnRequestEditForm = () => {
    return new FormGroup({
        id: new FormControl<number | undefined>({ value: undefined, disabled: false }, { nonNullable: true }),
        reason: new FormControl<string>(
            { value: '', disabled: false },
            { validators: [Validators.required], nonNullable: true }
        ),
        reviewerId: new FormControl<string | undefined>(
            { value: undefined, disabled: false },
            { validators: [Validators.required], nonNullable: true }
        ),
    });
};

@Component({
    selector: 'app-treatment-return-request-edit',
    standalone: true,
    imports: [CommonModule, EditorModule, TreatmentReviewerComponent, ReactiveFormsModule],
    templateUrl: './treatment-return-request-edit.component.html',
    styles: [
        `
            :host {
                height: 100%;
            }

            .attachment-upload-status {
                position: absolute;
                top: 0.75rem;
                right: 0.75rem;
                z-index: 1;
                width: min(22rem, calc(100% - 1.5rem));
                max-width: calc(100% - 1.5rem);
                padding: 0.5rem 0.75rem;
                border-radius: 0.375rem;
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 0 0.125rem 0.375rem rgba(0, 0, 0, 0.15);
            }

            .attachment-upload-progress {
                height: 0.375rem;
            }
        `,
    ],
    providers: [RxHelper],
})
export class TreatmentReturnRequestEditComponent implements OnChanges {
    @Input() isReviewerSelectDisabled = false;
    @Input() form = createTreatmentReturnRequestEditForm();
    @ViewChild('attachmentInput') private readonly attachmentInput?: ElementRef<HTMLInputElement>;

    protected isUploadingAttachment = false;
    protected uploadingAttachmentName?: string | null;
    protected attachmentUploadProgress = 0;
    private readonly _imageClient = inject(ImageClient);
    private _editor: any;

    ngOnChanges(changes: Record<keyof TreatmentReturnRequestEditComponent, SimpleChange>) {
        if (changes.isReviewerSelectDisabled) {
            if (this.isReviewerSelectDisabled) this.form.controls.reviewerId.disable();
            else this.form.controls.reviewerId.enable();
        }
    }

    protected onEditorInit({ editor }: EditorInitEvent) {
        this._editor = editor;
        editor.getModule('toolbar')?.addHandler('link', () => this.attachmentInput?.nativeElement.click());
    }

    protected onEditorClick(event: MouseEvent): void {
        if (!(event.target instanceof Element)) return;

        const link = event.target.closest<HTMLAnchorElement>('a[href]');
        if (!link?.href) return;

        event.preventDefault();
        window.open(link.href, '_blank', 'noopener,noreferrer');
    }

    protected async onAttachmentSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;
        if (!this.isSupportedAttachment(file)) return;

        this.isUploadingAttachment = true;
        this.uploadingAttachmentName = file.name;
        this.attachmentUploadProgress = 0;
        try {
            const url = await firstValueFrom(
                this._imageClient.upload(file, progress => (this.attachmentUploadProgress = progress))
            );
            this.attachmentUploadProgress = 100;
            this.insertAttachmentLink(file.name, url);
            this.form.controls.reason.markAsDirty();
        } finally {
            this.isUploadingAttachment = false;
            this.uploadingAttachmentName = null;
            this.attachmentUploadProgress = 0;
        }
    }

    private isSupportedAttachment(file: File): boolean {
        const supportedContentTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/zip',
            'application/x-zip-compressed',
            'video/mp4',
            'video/quicktime',
        ];
        const supportedExtensions = ['.pdf', '.docx', '.zip', '.mp4', '.mov'];
        const lowerCaseFileName = file.name.toLowerCase();
        return (
            supportedContentTypes.includes(file.type.toLowerCase()) ||
            supportedExtensions.some(extension => lowerCaseFileName.endsWith(extension))
        );
    }

    private insertAttachmentLink(fileName: string, url: string) {
        if (!this._editor) {
            const currentReason = this.form.controls.reason.value ?? '';
            const attachmentLink = `<p><a href="${url}" target="_blank">${this.escapeHtml(fileName)}</a></p>`;
            this.form.controls.reason.setValue(`${currentReason}${attachmentLink}`);
            return;
        }

        const range = this._editor.getSelection(true);
        const index = range?.index ?? this._editor.getLength();
        this._editor.insertText(index, fileName, 'link', url, 'user');
        this._editor.insertText(index + fileName.length, '\n', 'user');
        this._editor.setSelection(index + fileName.length + 1, 0, 'user');
    }

    private escapeHtml(value: string) {
        const div = document.createElement('div');
        div.innerText = value;
        return div.innerHTML;
    }
}
